/**
 * IhsCard — shared card chrome for the IHS match-detail widget bundle.
 *
 * Single shell so every IHS widget gets the same dark header bar, rounded
 * white surface, and consistent padding rhythm. Component-specific
 * content goes in `children`. Mirrors the chrome ported from
 * Hemmaklubben — see `apps/onsite-render/src/styles/animations.css` for
 * the staged-reveal cascade these `stage-*` classes opt into.
 *
 * Re-exports a few thin formatting helpers (`dashable`, `pct`, `avg`)
 * that compose with `utils/numbers.ts` so widgets keep tight call sites
 * while still routing through locale-aware formatters.
 */

import type { ReactElement, ReactNode } from 'react';
import { useInView } from '../../../hooks/useInView.js';
import { useTenantConfig } from '../../../tenant/useTenantConfig.js';
import { dashable as dashableImpl, formatPercent } from '../../../utils/numbers.js';
import styles from './IhsCard.module.css';

interface IhsCardProps {
  title: string;
  subtitle?: string;
  /**
   * `tight` reduces outer body padding for cards whose body is mostly
   * the toggle/list and full padding would create awkward double-space.
   */
  bodyVariant?: 'default' | 'tight';
  /** Optional row between header and body (toggle, badge group, etc.) */
  bar?: ReactNode;
  children: ReactNode;
}

export function IhsCard({
  title,
  subtitle,
  bodyVariant = 'default',
  bar,
  children,
}: IhsCardProps): ReactElement {
  // Stage-* classes opt the card into the global staged-reveal cascade.
  // The cascade fires when EITHER:
  //   1. The card itself has `.visible` — toggled by the local
  //      IntersectionObserver (used on the site), OR
  //   2. An ancestor has `.hk-revealed` (used by future video overlays
  //      where the player forces reveal on mount).
  // See animations.css for the full timing.
  const { ref, inView } = useInView<HTMLElement>({
    threshold: 0.05,
    rootMargin: '0px 0px 100px 0px', // pre-trigger BEFORE entering view (was -80px penetration — cards sat blank at the fold)
    once: true,
  });

  const bodyClass = bodyVariant === 'tight' ? styles.bodyTight : styles.body;

  // Widget-header subtitle consistency (2026-05-16):
  // when no subtitle is supplied by the engine OR the widget itself,
  // every IhsCard falls back to "competition · season" (e.g.
  // "Allsvenskan · 2026"). Operators told us inconsistent subtitle
  // text — some saying "7 matcher", some saying nothing, some saying
  // the league — looked sloppy. The rule: unless the numbers describe
  // a scope DIFFERENT from the season, show season text. Widgets that
  // legitimately span multiple seasons (e.g. H2H history) keep their
  // own override.
  const tenant = useTenantConfig();
  const resolvedSubtitle = subtitle ?? (
    tenant.kind === 'live' || tenant.kind === 'paused'
      ? `${tenant.config.competition.displayName} · ${tenant.config.competition.season}`
      : undefined
  );

  return (
    <section
      ref={ref}
      className={`${styles.card} stage-shell${inView ? ' visible' : ''}`}
    >
      <header className={`${styles.header} stage-header`}>
        <h2 className={styles.title}>{title}</h2>
        {resolvedSubtitle ? <span className={styles.subtitle}>{resolvedSubtitle}</span> : null}
      </header>
      {bar}
      <div className={`${bodyClass} stage-content`}>{children}</div>
    </section>
  );
}

/**
 * Render `value` if non-null, otherwise an em-dash. Optional formatter.
 * Re-exported from `utils/numbers.ts` so widget call sites stay terse.
 */
export const dashable = dashableImpl;

/** Format a 0..1 ratio as a percentage with no decimals. */
export const pct = formatPercent;

/**
 * Format a per-match mean to 1 decimal. The render layer flows the
 * tenant decimal separator through; widgets call the locale-aware
 * variant in `utils/numbers.ts` directly when they need the separator.
 * This thin wrapper is kept for parity with the source widgets which
 * import `avg()` from this module.
 */
export function avg(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return value.toFixed(1);
}

export { styles as ihsCardStyles };
